import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  Clock,
  Send,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { VerificationStatus } from '@/types/api';
import { cn } from '@/lib/utils';

interface VerificationBadgeProps {
  status?: VerificationStatus | null;
  eventConfirmed?: boolean;
  className?: string;
}

const statusConfig: Record<
  VerificationStatus,
  {
    label: string;
    icon: LucideIcon;
    className: string;
  }
> = {
  pending: {
    label: 'Pendiente',
    icon: Clock,
    className: 'bg-muted text-muted-foreground border-border',
  },
  sent: {
    label: 'Enviada',
    icon: Send,
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  },
  confirmed: {
    label: 'Confirmada',
    icon: CheckCircle,
    className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  },
  verified: {
    label: 'Verificada',
    icon: CheckCircle2,
    className: 'bg-green-500/10 text-green-700 border-green-500/20',
  },
  mismatch: {
    label: 'No coincide',
    icon: AlertTriangle,
    className: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  },
  missing: {
    label: 'Falta en dispositivo',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-600 border-red-500/20',
  },
  error: {
    label: 'Error',
    icon: AlertCircle,
    className: 'bg-red-500/10 text-red-700 border-red-500/30',
  },
};

export function VerificationBadge({
  status,
  eventConfirmed,
  className,
}: VerificationBadgeProps) {
  if (!status) return null;

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn('gap-1 text-xs font-normal', config.className, className)}
      title={
        eventConfirmed && status === 'confirmed'
          ? 'El dispositivo confirmó la recepción'
          : undefined
      }
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{config.label}</span>
    </Badge>
  );
}

export function isVerificationIssue(
  status?: VerificationStatus | null
): boolean {
  return status === 'mismatch' || status === 'missing' || status === 'error';
}

export function isVerificationTransitional(
  status?: VerificationStatus | null
): boolean {
  return status === 'pending' || status === 'sent' || status === 'confirmed';
}
